import React from "react";
import SpinningLoader from "../components/SpinningLoader";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useNotification } from "../context/NotificationProvider";

/**
 * PrivateRoute - protects routes requiring full authentication and onboarding
 * Redirects to appropriate onboarding steps if not complete
 */
const PrivateRoute = ({ children }) => {
  const { isAuthenticated, userProfile, refreshUser, loading, isLoggingOut } =
    useAuth();

  const { showMessage, showPersistentMessage } = useNotification();
  const location = useLocation();

  if (loading || isLoggingOut) {
    return <SpinningLoader />;
  }

  // Check authentication first - redirect to new login
  if (!isAuthenticated) {
    return <Navigate to="/landing" state={{ from: location }} replace />;
  }

  // If no user data, try to refresh once
  if (!userProfile) {
    refreshUser();
    return <SpinningLoader />;
  }

  // Check email verification
  if (!userProfile.emailVerified) {
    return <Navigate to="/onboarding/verify-email" replace />;
  }

  // Note: 2FA verification happens during login flow, not here.
  // If user has a valid token, they've already passed 2FA (if enabled).

  // Check role authorization (if role field exists)
  if (userProfile.role !== undefined && userProfile.role < 20) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Check profile completion
  if (!userProfile.profile_completed) {
    const storedEmails =
      JSON.parse(localStorage.getItem("verifiedEmails")) || [];
    const emailVerifiedFromStorage = storedEmails.includes(userProfile.email);
    if (emailVerifiedFromStorage) {
      return <Navigate to="/complete-profile" replace />;
    } else {
      return <Navigate to="/onboarding/welcome" replace />;
    }
  }

  // Check KYC/Veriff status
  const kycStatus = userProfile.veriffDetails?.status;

  if (!kycStatus) {
    return <Navigate to="/complete-profile" replace />;
  }

  if (kycStatus && kycStatus !== "approved" && kycStatus !== "submitted") {
    switch (kycStatus) {
      case "review":
        showPersistentMessage(
          "info",
          "Your identity verification request has been submitted and is pending approval"
        );
        break;

      case "resubmission_requested":
        showMessage(
          "warning",
          "There was an issue processing your verification request. Please resubmit your request"
        );
        return <Navigate to="/onboarding/welcome" replace />;

      case "expired":
        showMessage(
          "warning",
          "Your identity verification request expired. Please restart the process"
        );
        return <Navigate to="/onboarding/welcome" replace />;

      case "abandoned":
        showMessage(
          "warning",
          "Your identity verification request was abandoned. Please restart the process"
        );
        return <Navigate to="/onboarding/welcome" replace />;

      case "declined":
        showMessage(
          "error",
          "Your identity verification request was declined. Please restart the process"
        );
        return <Navigate to="/onboarding/welcome" replace />;

      case "created":
      case "started":
      case null:
      case undefined:
        return <Navigate to="/onboarding/welcome" replace />;

      default:
        // Unknown status, require verification
        return <Navigate to="/onboarding/welcome" replace />;
    }
  }

  // All checks passed, render children
  return children;
};

export default PrivateRoute;
